import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useTranslation } from "react-i18next";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { InputField } from "@/components/InputField/InputField";

import { CompanyEnrichType } from "@/enum/company-enrich-type.enum";

import {
	useCreateCompanyEnrichMutation,
	useUpdateCompanyEnrichMutation,
	usePreviewCompanyEnrichMutation,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";

import { PersonFiltersSection } from "./PersonFiltersSection";
import { CompanyFiltersSection } from "./CompanyFiltersSection";
import { FundingFiltersSection } from "./FundingFiltersSection";
import { CompanyLocationFiltersSection } from "./CompanyLocationFiltersSection";
import { AdvancedSearchFiltersSection } from "./AdvancedSearchFiltersSection";
import { CompanyEnrichModeSelector } from "./CompanyEnrichModeSelector";
import { CollapsibleFilterCard } from "./CollapsibleFilterCard";
import { LookalikeSeedCard } from "./LookalikeSeedCard";
import { FindSinglePersonHint } from "./FindSinglePersonHint";
import { ExcludeCompaniesSection } from "./ExcludeCompaniesSection";
import { NewInRoleTriggerSection } from "./NewInRoleTriggerSection";
import { normalizePayloadForMode, validateWorkforceGrowth } from "./companyEnrichPayload";
import {
	collapsedFilterSections,
	resolveVisibleFilterSections,
	VisibleCompanyEnrichSections,
} from "../utils/companyEnrichFilterSections";
import { getPreviewColumns } from "./companyEnrichPreviewColumns";
import { CompanyEnrichPreviewModal } from "./CompanyEnrichPreviewModal";
import { CompanyEnrichFormActions } from "./CompanyEnrichFormActions";
import { useCompanyEnrichLookups } from "./useCompanyEnrichLookups";
import { useCompanyEnrichFilterCounts } from "./useCompanyEnrichFilterCounts";
import { usePersonaCategories } from "./usePersonaCategories";
import { useSearchDefaultsPrefill } from "./useSearchDefaultsPrefill";
import { useSameAsCompanyLocation } from "./useSameAsCompanyLocation";
import { SearchDefaultsBar } from "./SearchDefaultsBar";
import { buildInitialFormValues, formValuesFromSource } from "../utils/companyEnrichFormValues";

import {
	CompanyEnrich,
	CreateCompanyEnrich,
	CreateCompanyEnrichSchema,
} from "../schema/companyEnrich";
import { List } from "../../List/schema/list";
import { resolveSourceMode } from "../../List/pages/ListCreate/utils/sourceMode";
import { WorkforceGrowthFiltersSection } from "./WorkforceGrowthFiltersSection";

interface CreateCompanyEnrichFormProps {
	companyEnrich?: CompanyEnrich | null;
	list: List;
	handleSelect?: (companyEnrich: CompanyEnrich | null) => void;
	handleDuplicate?: (companyEnrich: CompanyEnrich | null) => void;
}

export const CreateCompanyEnrichForm: FunctionComponent<CreateCompanyEnrichFormProps> = ({
	companyEnrich,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const [mode, setMode] = useState<CompanyEnrichType>(CompanyEnrichType.SEARCH);
	const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
	const [visibleSections, setVisibleSections] =
		useState<VisibleCompanyEnrichSections>(collapsedFilterSections);

	const toggleSection = (section: keyof VisibleCompanyEnrichSections) =>
		setVisibleSections((current) => ({ ...current, [section]: !current[section] }));

	const setSectionVisible = (section: keyof VisibleCompanyEnrichSections, isVisible: boolean) =>
		setVisibleSections((current) => ({ ...current, [section]: isVisible }));

	const [createCompanyEnrich, { error, isLoading }] = useCreateCompanyEnrichMutation();
	const [updateCompanyEnrich, { error: updateError, isLoading: updateIsLoading }] =
		useUpdateCompanyEnrichMutation();
	const [previewCompanyEnrich, { isLoading: previewIsLoading, data: previewData }] =
		usePreviewCompanyEnrichMutation();

	const {
		countryNameMap: countryNameMapRef,
		fetchPositions,
		fetchCountries,
		fetchSchools,
		fetchKeywords,
		fetchTechnologies,
		fetchCompanyDomains,
		fetchCompanyCountries,
		fetchStates,
		fetchCities,
		industriesData,
		industryOptions,
		geoRegions,
		regionOptions,
	} = useCompanyEnrichLookups();

	const initialState = buildInitialFormValues(list.id);

	const {
		reset,
		handleSubmit,
		setValue,
		getValues,
		watch,
		formState: { errors },
	} = useForm<CreateCompanyEnrich>({
		defaultValues: initialState,
		resolver: zodResolver(CreateCompanyEnrichSchema),
	});

	const formValues = watch();

	const { sameAsCompanyLocation, setSameAsCompanyLocation, syncSameAsCompanyLocation } =
		useSameAsCompanyLocation(formValues, setValue);

	const searchDefaults = useSearchDefaultsPrefill({
		getValues,
		reset,
		countryNameMap: countryNameMapRef,
		onApplied: (values) => {
			setVisibleSections(resolveVisibleFilterSections(values));
			syncSameAsCompanyLocation(values);
		},
	});

	useEffect(() => {
		if (companyEnrich) {
			const nextMode = companyEnrich.type ?? CompanyEnrichType.SEARCH;

			setMode(nextMode);

			const formState = formValuesFromSource(companyEnrich, list.id);

			if (handleDuplicate) {
				reset({ ...formState });
			} else {
				reset({ id: companyEnrich.id, ...formState });
			}

			// PREC-3868: expand whatever the saved source actually filters on, so an active
			// filter is never hidden behind a collapsed section.
			setVisibleSections(resolveVisibleFilterSections(formState));
			syncSameAsCompanyLocation(formState);
			searchDefaults.cancelPrefill();
		} else {
			const initialMode = resolveSourceMode(
				Object.values(CompanyEnrichType),
				CompanyEnrichType.SEARCH
			);

			setMode(initialMode);
			reset({ ...initialState, type: initialMode, listId: list.id });
			setVisibleSections(collapsedFilterSections());
			syncSameAsCompanyLocation(initialState);
			// PREC-3666: a new source opens with the user's last search, or the company preset.
			searchDefaults.requestPrefill();
		}
	}, [companyEnrich, list.id]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const handleModeChange = (nextMode: CompanyEnrichType) => {
		setMode(nextMode);
		setValue("type", nextMode, { shouldDirty: true, shouldValidate: true });
	};

	const onSubmit = async (values: CreateCompanyEnrich) => {
		try {
			const wgError = validateWorkforceGrowth(formValues);

			if (wgError) {
				setWorkforceGrowthError(wgError);
				setSectionVisible("workforceGrowth", true);

				return;
			}

			setWorkforceGrowthError(null);

			const payload = normalizePayloadForMode(values, mode);

			if (payload.id) {
				await updateCompanyEnrich({ id: payload.id, ...payload }).unwrap();
			} else {
				await createCompanyEnrich({ ...payload, listId: list.id }).unwrap();
				searchDefaults.rememberSearch(payload);
			}

			handleSelect?.(null);
			handleDuplicate?.(null);
			reset(initialState);
			searchDefaults.requestPrefill();
		} catch (err) {
			console.error(err);
		}
	};

	const previewColumns = useMemo(() => getPreviewColumns(ts), [ts]);

	const handlePreview = async () => {
		const wgError = validateWorkforceGrowth(formValues);

		if (wgError) {
			setWorkforceGrowthError(wgError);
			setSectionVisible("workforceGrowth", true);

			return;
		}

		setWorkforceGrowthError(null);
		searchDefaults.rememberSearch(formValues);
		const result = await previewCompanyEnrich({
			...formValues,
			listId: list.id,
			name: formValues.name || "Preview",
		}).unwrap();

		if (result) {
			setShowPreviewModal(true);
		}
	};

	const {
		personFilterCount,
		companyFilterCount,
		advancedSearchFilterCount,
		fundingFilterCount,
		workforceGrowthFilterCount,
		locationFilterCount,
		excludeCompaniesFilterCount,
	} = useCompanyEnrichFilterCounts(formValues);

	const buttonTitle =
		handleDuplicate && companyEnrich?.id
			? t("basics.copyCriteria")
			: companyEnrich?.id
				? t("basics.save")
				: t("basics.addCriteria");

	const filteredPersonaCategories = usePersonaCategories();

	const [workforceGrowthError, setWorkforceGrowthError] = useState<string | null>(null);

	return (
		<div className="flex flex-col gap-4 mt-8">
			{/* Use-case mode selector + source name */}
			<div className="p-4 rounded-xl border border-border-light bg-brand-white">
				<div className="text-md mb-4">{ts("addSource")}</div>
				<CompanyEnrichModeSelector mode={mode} onChange={handleModeChange} />
				<div className="text-ssm text-text-meta mt-3 mb-4">{ts(`modeDescription_${mode}`)}</div>

				<InputField
					error={!!errors.name?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("name", event.target.value, {
							shouldDirty: true,
							shouldValidate: true,
						});
					}}
					label={ts("sourceNameLabel")}
					name={"name"}
					placeholder={ts("namePlaceholder")}
					value={formValues.name ?? ""}
				/>
				<SearchDefaultsBar
					canResetToCompanyPreset={searchDefaults.canResetToCompanyPreset}
					isLoadingDefaults={searchDefaults.isLoadingDefaults}
					status={searchDefaults.status}
					onResetToCompanyPreset={searchDefaults.resetToCompanyPreset}
				/>
			</div>

			{mode === CompanyEnrichType.SEARCH && <FindSinglePersonHint />}

			{/* Lookalike seed companies — Lookalike search only */}
			{mode === CompanyEnrichType.LOOK_A_LIKE && (
				<LookalikeSeedCard
					fetchCompanyDomains={fetchCompanyDomains}
					formValues={formValues}
					setValue={setValue}
				/>
			)}

			{/* Person Filters - always visible card */}
			<div className="p-4 rounded-xl border border-border-light bg-brand-white">
				{mode === CompanyEnrichType.NEW_IN_ROLE && (
					<div className="mt-4">
						<NewInRoleTriggerSection formValues={formValues} setValue={setValue} />
					</div>
				)}

				<div className="text-smd text-text font-bold mb-2 mt-4 flex items-center gap-2">
					{ts("personFilters")}
					{personFilterCount > 0 && (
						<span className="inline-flex items-center justify-center rounded-full bg-blue-action px-2 py-0.5 text-xs font-medium text-white">
							{personFilterCount}
						</span>
					)}
				</div>
				<PersonFiltersSection
					formValues={formValues}
					setValue={setValue}
					fetchPositions={fetchPositions}
					fetchCountries={fetchCountries}
					fetchSchools={fetchSchools}
					countryNameMap={countryNameMapRef}
					personaCategories={filteredPersonaCategories}
					sameAsCompanyLocation={sameAsCompanyLocation}
					onSameAsCompanyLocationChange={setSameAsCompanyLocation}
				/>
			</div>

			{/* Company Details - collapsible card */}
			<CollapsibleFilterCard
				count={companyFilterCount}
				description={ts("companyFiltersDescription")}
				isOpen={visibleSections.company}
				title={ts("companyFilters")}
				onToggle={() => toggleSection("company")}
			>
				<CompanyFiltersSection
					formValues={formValues}
					setValue={setValue}
					industryOptions={industryOptions}
					industriesData={industriesData}
					fetchTechnologies={fetchTechnologies}
					fetchCompanyDomains={fetchCompanyDomains}
					showExcludeIndustries={visibleSections.excludeIndustries}
					setShowExcludeIndustries={(isVisible) =>
						setSectionVisible("excludeIndustries", isVisible)
					}
					excludeDomains={formValues.excludeDomains ?? []}
				/>
			</CollapsibleFilterCard>

			{/* Funding Data - collapsible card */}
			<CollapsibleFilterCard
				count={fundingFilterCount}
				description={ts("fundingFiltersDescription")}
				isOpen={visibleSections.funding}
				title={ts("fundingFilters")}
				onToggle={() => toggleSection("funding")}
			>
				<FundingFiltersSection formValues={formValues} setValue={setValue} />
			</CollapsibleFilterCard>

			{/* Company Location - collapsible card */}
			<CollapsibleFilterCard
				count={locationFilterCount}
				description={ts("locationFiltersDescription")}
				isOpen={visibleSections.location}
				title={ts("locationFilters")}
				onToggle={() => toggleSection("location")}
			>
				<CompanyLocationFiltersSection
					formValues={formValues}
					setValue={setValue}
					regionOptions={regionOptions}
					geoRegions={geoRegions}
					countryNameMap={countryNameMapRef}
					fetchCompanyCountries={fetchCompanyCountries}
					fetchStates={fetchStates}
					fetchCities={fetchCities}
				/>
			</CollapsibleFilterCard>

			{/* Advanced Search - collapsible card */}
			<CollapsibleFilterCard
				count={advancedSearchFilterCount}
				description={ts("advancedSearchDescription")}
				isOpen={visibleSections.advancedSearch}
				title={ts("advancedSearch")}
				onToggle={() => toggleSection("advancedSearch")}
			>
				<AdvancedSearchFiltersSection
					fetchKeywords={fetchKeywords}
					formValues={formValues}
					setValue={setValue}
					fetchCompanyDomains={fetchCompanyDomains}
					hideSimilarDomains={mode === CompanyEnrichType.LOOK_A_LIKE}
				/>
			</CollapsibleFilterCard>

			{/* Workforce Growth - collapsible card */}
			<CollapsibleFilterCard
				count={workforceGrowthFilterCount}
				description={ts("workforceGrowthFiltersDescription")}
				isOpen={visibleSections.workforceGrowth}
				title={ts("workforceGrowthFilters")}
				onToggle={() => toggleSection("workforceGrowth")}
			>
				<WorkforceGrowthFiltersSection
					formValues={formValues}
					setValue={setValue}
					externalError={workforceGrowthError}
					onErrorClear={() => setWorkforceGrowthError(null)}
				/>
			</CollapsibleFilterCard>

			<CollapsibleFilterCard
				count={excludeCompaniesFilterCount}
				description={ts("excludeCompaniesCardDescription")}
				isOpen={visibleSections.excludeCompanies}
				title={ts("excludeCompaniesCardTitle")}
				onToggle={() => toggleSection("excludeCompanies")}
			>
				<ExcludeCompaniesSection
					countryNameMap={countryNameMapRef}
					fetchCompanyDomains={fetchCompanyDomains}
					fetchCountries={fetchCountries}
					fetchPositions={fetchPositions}
					formValues={formValues}
					personaCategories={filteredPersonaCategories}
					setValue={setValue}
				/>
			</CollapsibleFilterCard>

			<CompanyEnrichFormActions
				previewLoading={previewIsLoading}
				submitLoading={isLoading || updateIsLoading}
				submitTitle={buttonTitle}
				onCancel={
					handleSelect
						? () => {
								handleSelect(null);
								handleDuplicate?.(null);
								reset(initialState);
							}
						: undefined
				}
				onPreview={handlePreview}
				onSubmit={handleSubmit(onSubmit)}
			>
				{getAllErrors(error || updateError, formErrors).length ? (
					<div className="mb-4">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
				) : null}
			</CompanyEnrichFormActions>

			{showPreviewModal && (
				<CompanyEnrichPreviewModal
					columns={previewColumns}
					data={previewData}
					isOpened={showPreviewModal}
					onClose={() => setShowPreviewModal(false)}
				/>
			)}
		</div>
	);
};
